import React, { useRef, useState } from "react";

// 쪽 위에 펜으로 그리기(2026-10-19 대표). 'Draw or stick a picture' 같은 칸에 아이가 직접 그린다.
// 지인에듀는 그리는 칸 자리를 주지 않아서 쪽 어디에나 그릴 수 있게 둔다.
//
// - 획은 쪽 비율 좌표(0~1)로 저장한다. 화면 크기 · 확대와 상관없이 같은 자리에 그려지고 해설지(readers_pdf)도 같다.
// - 그리기를 켠 동안만 손가락 · 마우스를 받는다. 끄면 그림은 보이기만 하고 아래 답 칸을 누를 수 있다.
// - 지우개는 손이 지나간 획을 통째로 지운다.

// 쪽 비율(A4, EbookPage 의 aspect-ratio 와 같다). viewBox 를 이 비율로 두어 획이 찌그러지지 않는다.
export const DRAWING_VIEW_WIDTH = 1000;
export const DRAWING_VIEW_HEIGHT = Math.round((1000 * 1170) / 827);
export const PEN_COLOR_LIST = ["#1F2328", "#E0312B", "#F08A24", "#F2C200", "#1F9D6B", "#2F6FDB", "#8B4FD6"];
// 펜 종류(2026-10-20 대표: 펜 · 붓펜처럼 다양하게, 굵기와 흐리기도). 굵기는 viewBox 단위(쪽 폭 1000)다.
// 붓펜은 천천히 그으면 굵고 빨리 그으면 가늘어진다. 형광펜은 굵고 비친다.
export const PEN_TOOL_LIST = [
  { key: "pencil", label: "연필", width: 3, opacity: 0.85 },
  { key: "pen", label: "펜", width: 6, opacity: 1 },
  { key: "brush", label: "붓펜", width: 13, opacity: 1 },
  { key: "marker", label: "형광펜", width: 30, opacity: 0.5 },
];
export const PEN_SIZE_LIST = [
  { key: "thin", label: "가늘게", scale: 0.55 },
  { key: "normal", label: "보통", scale: 1 },
  { key: "thick", label: "굵게", scale: 1.9 },
];
export const PEN_OPACITY_LIST = [
  { key: "solid", label: "진하게", opacity: 1 },
  { key: "soft", label: "중간", opacity: 0.6 },
  { key: "faint", label: "흐리게", opacity: 0.3 },
];
// 예전 획(종류 · 굵기 없이 저장된 것)의 굵기
export const PEN_WIDTH = 5;
// 붓펜 굵기가 따라 움직이는 폭. 한 점 사이 거리(viewBox 단위)가 길수록(빨리 그을수록) 가늘다
const BRUSH_WIDTH_RANGE = [0.4, 1.5];
const BRUSH_SPEED_DISTANCE = 30;
// 손이 조금 움직일 때마다 점을 다 찍으면 획이 무거워진다. 이만큼(쪽 폭 비율) 움직여야 점을 더한다.
const MINIMUM_POINT_DISTANCE = 0.003;
const MAXIMUM_POINT_COUNT = 600;
export const MAXIMUM_STROKE_COUNT = 300;
// 지우개가 닿았다고 보는 거리(viewBox 단위)
const ERASER_RADIUS = 22;

const roundRatio = (value) => Math.round(Math.min(Math.max(value, 0), 1) * 10000) / 10000;

export const strokePointsAttribute = (stroke) =>
  (stroke.points || [])
    .map(([x, y]) => `${(x * DRAWING_VIEW_WIDTH).toFixed(1)},${(y * DRAWING_VIEW_HEIGHT).toFixed(1)}`)
    .join(" ");

// 붓펜 획: 점 사이마다 짧은 선을 긋고, 그 사이 거리로 굵기를 정한다(앞 굵기와 섞어 갑자기 튀지 않게)
const brushSegmentListOf = (stroke) => {
  const pointList = (stroke.points || []).map(([x, y]) => [x * DRAWING_VIEW_WIDTH, y * DRAWING_VIEW_HEIGHT]);
  const segmentList = [];
  let previousScale = 1.2;
  for (let index = 1; index < pointList.length; index += 1) {
    const [fromX, fromY] = pointList[index - 1];
    const [toX, toY] = pointList[index];
    const distance = Math.hypot(toX - fromX, toY - fromY);
    const targetScale = Math.min(
      Math.max(BRUSH_WIDTH_RANGE[1] - distance / BRUSH_SPEED_DISTANCE, BRUSH_WIDTH_RANGE[0]),
      BRUSH_WIDTH_RANGE[1]
    );
    previousScale = previousScale * 0.6 + targetScale * 0.4;
    segmentList.push({ fromX, fromY, toX, toY, width: stroke.width * previousScale });
  }
  return segmentList;
};

export const StrokeShape = ({ stroke }) => {
  const width = stroke.width || PEN_WIDTH;
  const opacity = stroke.opacity ?? 1;
  if (stroke.tool === "brush" && (stroke.points || []).length > 2) {
    return (
      <g opacity={opacity} stroke={stroke.color} strokeLinecap="round">
        {brushSegmentListOf({ ...stroke, width }).map((segment, index) => (
          <line
            key={index}
            x1={segment.fromX.toFixed(1)}
            y1={segment.fromY.toFixed(1)}
            x2={segment.toX.toFixed(1)}
            y2={segment.toY.toFixed(1)}
            strokeWidth={segment.width.toFixed(1)}
          />
        ))}
      </g>
    );
  }
  return (
    <polyline
      points={strokePointsAttribute(stroke)}
      fill="none"
      stroke={stroke.color}
      strokeWidth={width}
      strokeOpacity={opacity}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
};

// pen: { tool, color, width, opacity } 지금 고른 펜
const DrawingLayer = ({ strokeList, isDrawing, pen, isEraser, onChange }) => {
  const svgRef = useRef(null);
  const drawingRef = useRef(null);
  const [liveStroke, setLiveStroke] = useState(null);

  const ratioPointOf = (event) => {
    const rect = svgRef.current.getBoundingClientRect();
    return [roundRatio((event.clientX - rect.left) / rect.width), roundRatio((event.clientY - rect.top) / rect.height)];
  };

  const eraseAt = ([x, y]) => {
    const remaining = strokeList.filter(
      (stroke) =>
        !(stroke.points || []).some(
          ([pointX, pointY]) =>
            Math.hypot((pointX - x) * DRAWING_VIEW_WIDTH, (pointY - y) * DRAWING_VIEW_HEIGHT) < ERASER_RADIUS
        )
    );
    if (remaining.length !== strokeList.length) onChange(remaining);
  };

  const handlePointerDown = (event) => {
    if (!isDrawing) return;
    event.preventDefault();
    svgRef.current.setPointerCapture(event.pointerId);
    const point = ratioPointOf(event);
    if (isEraser) {
      drawingRef.current = { isErasing: true };
      eraseAt(point);
      return;
    }
    if (strokeList.length >= MAXIMUM_STROKE_COUNT) return;
    drawingRef.current = { ...pen, points: [point] };
    setLiveStroke(drawingRef.current);
  };

  const handlePointerMove = (event) => {
    const current = drawingRef.current;
    if (!isDrawing || !current) return;
    const point = ratioPointOf(event);
    if (current.isErasing) {
      eraseAt(point);
      return;
    }
    const last = current.points[current.points.length - 1];
    if (Math.hypot(point[0] - last[0], point[1] - last[1]) < MINIMUM_POINT_DISTANCE) return;
    if (current.points.length >= MAXIMUM_POINT_COUNT) return;
    drawingRef.current = { ...current, points: [...current.points, point] };
    setLiveStroke(drawingRef.current);
  };

  const handlePointerUp = () => {
    const current = drawingRef.current;
    drawingRef.current = null;
    setLiveStroke(null);
    if (!current || current.isErasing) return;
    // 한 번 톡 찍은 것도 점으로 남긴다
    const points = current.points.length === 1 ? [current.points[0], current.points[0]] : current.points;
    onChange([...strokeList, { ...current, points }]);
  };

  return (
    <svg
      ref={svgRef}
      className={`page-drawing${isDrawing ? " page-drawing--active" : ""}${isEraser ? " page-drawing--eraser" : ""}`}
      viewBox={`0 0 ${DRAWING_VIEW_WIDTH} ${DRAWING_VIEW_HEIGHT}`}
      preserveAspectRatio="none"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {[...strokeList, ...(liveStroke ? [liveStroke] : [])].map((stroke, index) => (
        <StrokeShape key={index} stroke={stroke} />
      ))}
    </svg>
  );
};

export default DrawingLayer;
